import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import breakpoints from "@shared/theme/props/breakpoints";
import CartSummaryModal from "@app-contexts/public/features/basket/components/CartSummaryModal/CartSummaryModal.web";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import CartDiscountMessage from "@app-contexts/public/features/discounts/components/CartDiscountMessage/CartDiscountMessage.web";
import { NestedPublicVenueSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import { PUBLIC_ROUTES } from "@app-contexts/public/constants/public-routes";
import Button from "@ui-kit/form/Button/Button.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Modal from "@ui-kit/overlays/Modal/Modal.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import useRedirect from "@shared/hooks/useRedirect";
import React from "react";
import { useSubOptionsOptional } from "@shared/contexts/SubOptionsContext/SubOptionsContext";
import { CheckoutLabel } from "../shared/CheckoutLabel.web";

const { sm } = breakpoints;

export type DetailPageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  itemListElement: JSX.Element;
  venue?: NestedPublicVenueSerializer;
  holdLink?: string;
  dynamicHoldParams?: { purchase_platform: string; payment_type: string };
  hasAdditionalItems?: boolean;
  onNextToSubOptions?: () => void;
};

/**
 * Modal wrapper for any item list available from the public purchase pages (memberships, tickets, products)
 * For use on regular event pages or membership purchase pages, not attraction pages
 */
export const DetailPageModal: React.FC<DetailPageModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle = undefined,
  itemListElement,
  venue,
  holdLink = undefined,
  dynamicHoldParams = undefined,
  hasAdditionalItems = false,
  onNextToSubOptions = undefined,
}): JSX.Element => {
  const { t } = useTranslation("purchases");
  const { redirect } = useRedirect();
  const [isSmallerThanSm] = useMediaQuery(`(max-width: ${sm}px)`);
  const navigateToCheckout = () => redirect(PUBLIC_ROUTES.CHECKOUT.ROOT);

  const { hasItems, discountMessageObject, isBasketUpdating } =
    usePublicBasket();

  // Use sub-options context for state management (optional - won't throw if not in provider)
  const subOptionsContext = useSubOptionsOptional();
  const isViewingSubOptions = subOptionsContext?.isViewingSubOptions || false;
  const hasSubOptionChildren = subOptionsContext?.hasSubOptionChildren || false;
  const showSubOptions = subOptionsContext?.showSubOptions;

  const {
    isOpen: isCartSummaryModalOpen,
    onOpen: onOpenCartSummaryModal,
    onClose: onCloseCartSummaryModal,
  } = useDisclosure();

  const isDynamicHold = !!holdLink;

  const getCloseButtonLabel = (): string => {
    if (isDynamicHold) {
      return hasAdditionalItems ? t("Continue Shopping") : t("Close");
    }
    return t("View Cart");
  };

  const handleNextToSubOptions = (): void => {
    showSubOptions?.();
    onNextToSubOptions?.();
  };

  return (
    <>
      <CartSummaryModal
        isOpen={isCartSummaryModalOpen}
        onClose={onCloseCartSummaryModal}
        venue={venue}
        holdLink={holdLink}
        dynamicHoldParams={dynamicHoldParams}
      />
      <Modal
        scrollBehavior="inside"
        isOpen={isOpen}
        isCentered
        onClose={onClose}
        size={isSmallerThanSm ? "full" : "3xl"}
        variant="interactive"
      >
        <Modal.Header tabIndex={0}>
          <Flex alignItems="center" justifyContent="space-between">
            <Flex direction="column">
              <Text fontSize={{ base: "md", sm: "xl" }}>{title}</Text>
              {!isViewingSubOptions && (
                <Text fontSize={{ base: "sm", sm: "md" }} fontWeight="normal">
                  {subtitle}
                </Text>
              )}
            </Flex>
          </Flex>
        </Modal.Header>
        {discountMessageObject &&
          Object.keys(discountMessageObject).length > 0 && (
            <Box data-testid="detail-page-modal-discount-message-element">
              <CartDiscountMessage
                discountMessageObject={discountMessageObject}
              />
            </Box>
          )}
        <Modal.Body>{itemListElement}</Modal.Body>

        <Modal.Footer>
          <Flex w="full" justifyContent="space-between">
            <Button.Outline
              data-testid="detail-page-modal-close"
              // TODO: fix hardcoded style values
              // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
              minWidth="147px"
              colorPalette="primary"
              onClick={() => {
                if (isDynamicHold) {
                  onClose();
                  return;
                }

                onOpenCartSummaryModal();
              }}
              visibility={hasItems || isDynamicHold ? "visible" : "hidden"}
            >
              {getCloseButtonLabel()}
            </Button.Outline>
            {hasSubOptionChildren && showSubOptions && !isViewingSubOptions ? (
              <Button
                data-testid="detail-page-modal-next"
                // TODO: fix hardcoded style values
                // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
                minWidth="147px"
                colorPalette="primary"
                variant="solid"
                disabled={!hasItems || isBasketUpdating}
                onClick={handleNextToSubOptions}
              >
                {t("Continue")}
              </Button>
            ) : (
              <Button
                data-testid="detail-page-modal-checkout"
                // TODO: fix hardcoded style values
                // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
                minWidth="147px"
                colorPalette="primary"
                variant="solid"
                disabled={!hasItems || isBasketUpdating}
                onClick={navigateToCheckout}
              >
                <CheckoutLabel />
              </Button>
            )}
          </Flex>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DetailPageModal;
